import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Upload } from "lucide-react";
import api from "../../api/client";
import LangTabs, { LANG_CODES } from "./LangTabs";

const CATEGORIES = [
  { key: "Services", labels: { fr: "Services", en: "Services", nl: "Diensten", it: "Servizi" } },
  { key: "Actualités", labels: { fr: "Actualités", en: "News", nl: "Nieuws", it: "Notizie" } },
  { key: "Recrutement", labels: { fr: "Recrutement", en: "Recruitment", nl: "Werving", it: "Reclutamento" } },
  { key: "Clinique", labels: { fr: "Clinique", en: "Clinic", nl: "Kliniek", it: "Clinica" } },
];

const empty = () => ({
  title: "", excerpt: "", content: "", image: "/images/news/", category: "Actualités",
  i18n: { fr: { title: "", excerpt: "", content: "", category: "Actualités" },
          en: { title: "", excerpt: "", content: "", category: "News" },
          nl: { title: "", excerpt: "", content: "", category: "Nieuws" },
          it: { title: "", excerpt: "", content: "", category: "Notizie" } },
});

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [lang, setLang] = useState("fr");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get("/blog?limit=100").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (post) => {
    const i18n = post.i18n || {};
    LANG_CODES.forEach((l) => {
      i18n[l] = i18n[l] || { title: "", excerpt: "", content: "", category: "" };
    });
    setEditing({ ...post, i18n });
    setLang("fr");
    setErr("");
  };

  const startCreate = () => { setEditing(empty()); setLang("fr"); setErr(""); };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const payload = {
        title: editing.i18n.fr.title || editing.title,
        excerpt: editing.i18n.fr.excerpt || editing.excerpt,
        content: editing.i18n.fr.content || editing.content,
        image: editing.image,
        category: editing.i18n.fr.category || editing.category,
        i18n: editing.i18n,
      };
      if (editing.slug) {
        await api.put(`/blog/${editing.slug}`, payload);
      } else {
        await api.post("/blog", payload);
      }
      setEditing(null);
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await api.delete(`/blog/${slug}`);
    load();
  };

  const uploadImage = (file) => {
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { setErr("Image trop grande (max 1.5 Mo)"); return; }
    const reader = new FileReader();
    reader.onload = (e) => setEditing((cur) => ({ ...cur, image: e.target.result }));
    reader.readAsDataURL(file);
  };

  const setI18n = (l, field, val) =>
    setEditing((e) => ({ ...e, i18n: { ...e.i18n, [l]: { ...e.i18n[l], [field]: val } } }));

  const setCategory = (key) => {
    const cat = CATEGORIES.find((c) => c.key === key);
    if (!cat) return;
    setEditing((e) => ({
      ...e,
      category: cat.key,
      i18n: {
        ...e.i18n,
        fr: { ...e.i18n.fr, category: cat.labels.fr },
        en: { ...e.i18n.en, category: cat.labels.en },
        nl: { ...e.i18n.nl, category: cat.labels.nl },
        it: { ...e.i18n.it, category: cat.labels.it },
      },
    }));
  };

  return (
    <div data-testid="blog-admin">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-slate-900">Articles du blog</h1>
          <p className="text-slate-500 text-sm mt-1">{posts.length} article(s)</p>
        </div>
        <button onClick={startCreate} className="gradient-bg text-white text-sm font-semibold px-5 py-2.5 rounded-full flex items-center gap-2" data-testid="blog-create-btn">
          <Plus className="w-4 h-4" /> Nouvel article
        </button>
      </div>

      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="bg-white border border-slate-100 rounded-2xl p-4 flex items-center gap-4">
            <img src={p.image || "/images/symbol.png"} alt="" className="w-16 h-16 rounded-xl object-cover bg-slate-100" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 truncate">{p.title}</p>
              <p className="text-xs text-slate-500 truncate">{p.category} · {p.slug}</p>
            </div>
            <button onClick={() => startEdit(p)} className="p-2 rounded-full hover:bg-slate-50 text-slate-600" data-testid={`edit-${p.slug}`}>
              <Pencil className="w-4 h-4" />
            </button>
            <button onClick={() => remove(p.slug)} className="p-2 rounded-full hover:bg-rose-50 text-rose-600">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="blog-editor-modal">
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8 my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit text-2xl font-bold text-slate-900">
                {editing.slug ? "Modifier l'article" : "Nouvel article"}
              </h2>
              <button onClick={() => setEditing(null)} className="p-2 rounded-full hover:bg-slate-50">
                <X className="w-5 h-5" />
              </button>
            </div>

            {err && <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-2xl p-3">{err}</div>}

            <div className="mb-5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Image de couverture</label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-24 h-24 rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0">
                  {editing.image && <img src={editing.image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 space-y-2">
                  <label className="inline-flex items-center gap-2 border border-slate-200 px-4 py-2 rounded-full text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-50">
                    <Upload className="w-4 h-4" /> Téléverser (max 1.5 Mo)
                    <input type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files?.[0])} className="hidden" data-testid="blog-image-upload" />
                  </label>
                  <input type="text" value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })}
                    placeholder="ou collez une URL / chemin /images/news/..."
                    className="w-full px-4 py-2 border border-slate-200 rounded-2xl text-sm" data-testid="blog-image-input" />
                </div>
              </div>
            </div>

            <div className="mb-5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Catégorie</label>
              <select
                value={CATEGORIES.find((c) => c.key === (editing.category || editing.i18n?.fr?.category))?.key || "Actualités"}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm"
                data-testid="blog-category-select"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>{c.key}</option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-1">Traduite automatiquement dans les 4 langues.</p>
            </div>

            <LangTabs active={lang} onChange={setLang} />

            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Titre ({lang.toUpperCase()})</label>
                <input type="text" value={editing.i18n[lang].title} onChange={(e) => setI18n(lang, "title", e.target.value)}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm" data-testid={`blog-title-${lang}`} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Extrait ({lang.toUpperCase()})</label>
                <textarea value={editing.i18n[lang].excerpt} onChange={(e) => setI18n(lang, "excerpt", e.target.value)} rows={2}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Contenu HTML ({lang.toUpperCase()})</label>
                <textarea value={editing.i18n[lang].content} onChange={(e) => setI18n(lang, "content", e.target.value)} rows={10}
                  className="w-full mt-1 px-4 py-2.5 border border-slate-200 rounded-2xl text-sm font-mono" placeholder="<p>...</p>" />
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 rounded-full">
                Annuler
              </button>
              <button onClick={save} disabled={saving} className="gradient-bg text-white text-sm font-semibold px-5 py-2.5 rounded-full flex items-center gap-2 disabled:opacity-60" data-testid="blog-save-btn">
                <Save className="w-4 h-4" /> {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
